import { useEffect, useState, type FormEvent } from 'react';
import { api, type Client, type ClientWriteInput } from '../../lib/api';
import {
  formatRateFromCents,
  parseRateInput,
  rateInputValue,
} from '../../lib/format';
import { Modal } from '../../components/Modal';

type ViewState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; clients: Client[] };

export function ClientsSection() {
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [editing, setEditing] = useState<Client | 'new' | null>(null);

  async function refresh() {
    setState({ kind: 'loading' });
    try {
      const clients = await api.listClients();
      setState({ kind: 'ready', clients });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function handleDelete(client: Client) {
    const ok = window.confirm(
      `Delete "${client.name}"? Projects linked to it will keep tracking, but their billing defaults will reset.`
    );
    if (!ok) return;
    try {
      await api.deleteClient(client.id);
      await refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <section className="mt-10">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-display text-sub font-medium tracking-sub text-black">
          Clients
        </h2>
        <button type="button" onClick={() => setEditing('new')} className="btn-primary !py-1.5 !px-3">
          <span className="mono-label">+ New client</span>
        </button>
      </div>

      {state.kind === 'loading' && (
        <div className="py-8 flex items-center justify-center">
          <span className="mono-label text-black/50">loading…</span>
        </div>
      )}

      {state.kind === 'error' && (
        <div className="card p-5">
          <p className="font-display text-body text-black/70">
            Could not load clients: {state.message}
          </p>
        </div>
      )}

      {state.kind === 'ready' && state.clients.length === 0 && (
        <div className="card p-8 text-center">
          <span className="mono-label text-black/50 block mb-3">no clients yet</span>
          <p className="font-display text-body text-black/60 max-w-md mx-auto tracking-body">
            Add your first client to start assigning projects and setting default
            billing rates.
          </p>
        </div>
      )}

      {state.kind === 'ready' && state.clients.length > 0 && (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-glass-dark">
              <tr>
                <Th>Name</Th>
                <Th>Standard</Th>
                <Th>Human</Th>
                <Th>Agent</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-light">
              {state.clients.map((client) => (
                <tr key={client.id} className="hover:bg-glass-dark/50 transition-colors">
                  <td className="px-5 py-3 font-display text-body font-medium">
                    {client.name}
                  </td>
                  <td className="px-5 py-3 font-mono text-[11px] text-black/60 tabular-nums">
                    {formatRateFromCents(client.default_standard_rate_cents)}
                  </td>
                  <td className="px-5 py-3 font-mono text-[11px] text-black/60 tabular-nums">
                    {formatRateFromCents(client.default_human_rate_cents)}
                  </td>
                  <td className="px-5 py-3 font-mono text-[11px] text-black/60 tabular-nums">
                    {formatRateFromCents(client.default_agent_rate_cents)}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-4">
                      <button
                        type="button"
                        onClick={() => setEditing(client)}
                        className="font-display text-caption text-midnight hover:underline"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(client)}
                        className="font-display text-caption text-[#b91c1c] hover:underline"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <ClientFormModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      )}
    </section>
  );
}

function ClientFormModal({
  initial,
  onClose,
  onSaved,
}: {
  initial: Client | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [standardRate, setStandardRate] = useState(
    rateInputValue(initial?.default_standard_rate_cents)
  );
  const [humanRate, setHumanRate] = useState(rateInputValue(initial?.default_human_rate_cents));
  const [agentRate, setAgentRate] = useState(rateInputValue(initial?.default_agent_rate_cents));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    const payload: ClientWriteInput = {
      name: name.trim(),
      default_standard_rate_cents: parseRateInput(standardRate),
      default_human_rate_cents: parseRateInput(humanRate),
      default_agent_rate_cents: parseRateInput(agentRate),
    };
    setSaving(true);
    try {
      if (initial) {
        await api.updateClient(initial.id, payload);
      } else {
        await api.createClient(payload);
      }
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={initial ? `Edit ${initial.name}` : 'New client'}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            form="client-form"
            disabled={saving}
            className="btn-primary"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="client-form" onSubmit={handleSubmit} className="space-y-5">
        <label className="block">
          <div className="mono-label text-black/50 mb-2">Name</div>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Inc"
            maxLength={128}
            className="input"
          />
        </label>

        <div className="pt-3 border-t border-border-light">
          <div className="mono-label text-black/50 mb-3">Default rates (USD/hour)</div>
          <p className="font-display text-caption text-black/60 mb-4 tracking-body">
            Used as fallbacks when a project linked to this client doesn't override.
            Leave blank to inherit nothing.
          </p>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Standard">
              <input
                type="number"
                min={0}
                step="0.01"
                value={standardRate}
                onChange={(e) => setStandardRate(e.target.value)}
                placeholder="—"
                className="input"
              />
            </Field>
            <Field label="Human">
              <input
                type="number"
                min={0}
                step="0.01"
                value={humanRate}
                onChange={(e) => setHumanRate(e.target.value)}
                placeholder="—"
                className="input"
              />
            </Field>
            <Field label="Agent">
              <input
                type="number"
                min={0}
                step="0.01"
                value={agentRate}
                onChange={(e) => setAgentRate(e.target.value)}
                placeholder="—"
                className="input"
              />
            </Field>
          </div>
        </div>

        {error && <p className="font-display text-caption text-[#b91c1c]">{error}</p>}
      </form>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mono-label text-black/50 mb-2">{label}</div>
      {children}
    </label>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`mono-label text-black/50 text-left px-5 py-3 ${className ?? ''}`}>
      {children}
    </th>
  );
}
